import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Phone, Clock, Shield, Heart, Users, AlertTriangle,
  CheckCircle, ArrowRight, MapPin, Loader2
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { mockStates, specializations } from '../data/mock';
import { apiCall } from '../utils/api';

const GetHelpPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    state: '',
    addiction: '',
    urgency: '',
    insurance: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      await apiCall('/api/help-requests', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError('Something went wrong submitting your request. Please call us directly using the numbers above instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Get Help Now - You're Not Alone
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Take the first step toward recovery. Our treatment coordinators are standing by 
              to help you find the right treatment program for your needs.
            </p>
            
            {/* Emergency Numbers */}
            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <Phone className="h-5 w-5 mr-2" />
                  <span className="font-semibold">Crisis Support</span>
                </div>
                <a 
                  href="tel:1-888-778-2494"
                  className="text-xl font-bold hover:underline"
                >
                  1-888-778-2494
                </a>
                <div className="text-sm text-blue-100">Call us Now</div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <Phone className="h-5 w-5 mr-2" />
                  <span className="font-semibold">Treatment Help</span>
                </div>
                <a 
                  href="tel:888-968-1014"
                  className="text-xl font-bold hover:underline"
                >
                  888-968-1014
                </a>
                <div className="text-sm text-blue-100">Call us Now</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Heart className="h-6 w-6 mr-3 text-red-500" />
                  Get Personalized Treatment Help
                </CardTitle>
                <p className="text-gray-600">
                  Fill out this confidential form and a treatment coordinator will contact you 
                  within 24 hours to discuss your options.
                </p>
              </CardHeader>
              
              <CardContent>
                {submitSuccess ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Thank you for reaching out
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      A treatment coordinator will contact you within 24 hours. If this is urgent,
                      please call one of the numbers above right away.
                    </p>
                  </div>
                ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name</label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone Number</label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">State</label>
                      <Select value={formData.state} onValueChange={(value) => handleInputChange('state', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your state" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockStates.map((state) => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Primary Addiction Concern</label>
                      <Select value={formData.addiction} onValueChange={(value) => handleInputChange('addiction', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select addiction type" />
                        </SelectTrigger>
                        <SelectContent>
                          {specializations.slice(0, 8).map((spec) => (
                            <SelectItem key={spec} value={spec}>
                              {spec}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Urgency Level</label>
                      <Select value={formData.urgency} onValueChange={(value) => handleInputChange('urgency', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="How urgent is your need?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Immediate - Need help today</SelectItem>
                          <SelectItem value="week">Within a week</SelectItem>
                          <SelectItem value="month">Within a month</SelectItem>
                          <SelectItem value="exploring">Just exploring options</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Insurance Status</label>
                      <Select value={formData.insurance} onValueChange={(value) => handleInputChange('insurance', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Do you have insurance?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="private">Private Insurance</SelectItem>
                          <SelectItem value="medicare">Medicare</SelectItem>
                          <SelectItem value="medicaid">Medicaid</SelectItem>
                          <SelectItem value="none">No Insurance</SelectItem>
                          <SelectItem value="unsure">Not Sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Additional Information (Optional)
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Tell us about your situation, treatment preferences, or any questions you have..."
                      rows={4}
                    />
                  </div>

                  {submitError && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertDescription className="text-red-700">
                        {submitError}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get Treatment Help Now
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-600 text-center">
                    By submitting this form, you agree to receive communication from our treatment coordinators.
                    Your information is kept strictly confidential.
                  </p>
                </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Why Get Help */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-blue-600" />
                  Why Get Help Now?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Free Consultation</div>
                    <div className="text-xs text-gray-600">No cost to speak with our coordinators</div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Confidential Support</div>
                    <div className="text-xs text-gray-600">Your privacy is protected</div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Insurance Help</div>
                    <div className="text-xs text-gray-600">We help verify your benefits</div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Personalized Matching</div>
                    <div className="text-xs text-gray-600">Find the right fit for you</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Resources */}
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="flex items-center text-red-800">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Need Immediate Help?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <div className="font-medium text-red-800 mb-1">Crisis Support</div>
                  <a href="tel:988" className="text-red-600 font-bold text-lg hover:underline">
                    988 - Crisis Lifeline
                  </a>
                  <div className="text-xs text-red-700">24/7 mental health crisis support</div>
                </div>
                
                <div className="text-sm">
                  <div className="font-medium text-red-800 mb-1">Poison Control</div>
                  <a href="tel:1-800-222-1222" className="text-red-600 font-bold hover:underline">
                    1-800-222-1222
                  </a>
                  <div className="text-xs text-red-700">For overdose emergencies</div>
                </div>
                
                <div className="text-sm">
                  <div className="font-medium text-red-800 mb-1">Emergency Services</div>
                  <a href="tel:911" className="text-red-600 font-bold text-lg hover:underline">
                    911
                  </a>
                  <div className="text-xs text-red-700">Life-threatening emergencies</div>
                </div>
              </CardContent>
            </Card>

            {/* Success Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="h-5 w-5 mr-2 text-purple-600" />
                  Recovery Success
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">85%</div>
                  <div className="text-sm text-gray-600">Treatment Success Rate</div>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">23.5M</div>
                  <div className="text-sm text-gray-600">Americans in Recovery</div>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">24/7</div>
                  <div className="text-sm text-gray-600">Support Available</div>
                </div>
              </CardContent>
            </Card>

            {/* Browse Options */}
            <Card>
              <CardHeader>
                <CardTitle>Browse Treatment Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button asChild variant="outline" size="sm" className="w-full justify-start">
                  <Link to="/search">
                    <MapPin className="h-4 w-4 mr-2" />
                    Find Local Treatment
                  </Link>
                </Button>
                
                <Button asChild variant="outline" size="sm" className="w-full justify-start">
                  <Link to="/treatment-types">
                    <Users className="h-4 w-4 mr-2" />
                    Treatment Types
                  </Link>
                </Button>
                
                <Button asChild variant="outline" size="sm" className="w-full justify-start">
                  <Link to="/insurance">
                    <Shield className="h-4 w-4 mr-2" />
                    Insurance Help
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetHelpPage;